import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchTokenPurchases,
  fetchTrades,
  fetchPosts,
  fetchMessages,
  fetchFollowers,
  fetchNotifications,
  fetchEngagement,
  fetchRoyalties,
  fetchContentEarnings,
  fetchProfileViews,
  fetchShareClicks,
  fetchReputation,
} from '../thunks/analyticsThunks';

export type TimeRange = '24h' | '7d' | '30d' | '90d' | 'all';

export type AnalyticsCategory =
  | 'royalties'
  | 'content-earnings'
  | 'trades'
  | 'posts'
  | 'messages'
  | 'followers'
  | 'notifications'
  | 'engagement'
  | 'token-purchases'
  | 'profile-views'
  | 'share-clicks'
  | 'reputation';

export interface TokenPurchaseAnalytics {
  id: string;
  token_amount: string;
  usd_price: string;
  payment_method: string;
  payment_status: string;
  blockchain_tx_hash: string | null;
  recipient_wallet: string;
  tokens_distributed: boolean;
  distributed_at: string | null;
  created_at: string;
  pack_name: string;
  pack_description: string;
}

export interface TradeAnalytics {
  id: string;
  from_user_id: string;
  to_user_id: string;
  price: string;
  trade_type: string;
  transaction_hash: string;
  creator_royalty: string;
  platform_fee: string;
  created_at: string;
  metadata_uri: string;
  from_username: string;
  to_username: string;
}

export interface PostAnalytics {
  id: string;
  owner_id: string;
  posted_by: string;
  body: string;
  media_url: string | null;
  created_at: string;
  updated_at: string;
  author_username: string;
  likes_count: number;
  dislikes_count: number;
  comments_count: number;
}

export interface MessageAnalytics {
  id: string;
  thread_id: string;
  sender_id: string;
  body: string;
  read: boolean;
  created_at: string;
  sender_username: string;
  direction: 'sent' | 'received';
}

export interface FollowerItem {
  follower_id?: string;
  following_id?: string;
  created_at: string;
  username: string;
  avatar_url: string | null;
}

export interface FollowerAnalytics {
  followers: {
    count: number;
    list: FollowerItem[];
  };
  following: {
    count: number;
    list: FollowerItem[];
  };
}

export interface NotificationAnalytics {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  read_at: string | null;
  from_user_id: string | null;
  post_id: string | null;
  created_at: string;
}

export interface EngagementAnalytics {
  likesGiven: number;
  likesReceived: number;
  dislikesGiven: number;
  dislikesReceived: number;
  commentsGiven: number;
  commentsReceived: number;
  favoritesGiven: number;
  favoritesReceived: number;
}

export interface RoyaltyRecord {
  id: string;
  amount: number;
  source: 'profile_sale' | 'spotlight';
  profileId?: string;
  profileUsername?: string;
  buyerUsername?: string;
  salePrice?: number;
  spotlightType?: 'dm_spotlight' | 'post_spotlight' | 'profile_spotlight';
  createdAt: string;
}

export interface ContentSaleRecord {
  id: string;
  type: 'ppv';
  amount: number;
  tokenId: string;
  txHash: string;
  postId: string;
  postPreview: string;
  buyerUsername: string | null;
  buyerDisplayName: string | null;
  createdAt: string;
}

export interface AccessSaleRecord {
  id: string;
  type: 'view_fee' | 'subscription';
  amount: number;
  txHash: string;
  buyerUsername: string | null;
  buyerDisplayName: string | null;
  createdAt: string;
}

export interface ContentEarningsAnalytics {
  stats: {
    totalAllTime: number;
    periodEarned: number;
    salesCount: number;
    uniqueBuyers: number;
    viewFeeSalesCount: number;
    subscriptionSalesCount: number;
    viewFeeAllTime: number;
    subscriptionAllTime: number;
  };
  sales: ContentSaleRecord[];
  viewFeeSales: AccessSaleRecord[];
  subscriptionSales: AccessSaleRecord[];
  timeRange: TimeRange;
}

/**
 * Mirrors UserProfileController.VALID_VIEW_SOURCES. Loose-typed because
 * legacy rows pre-#213 have NULL source — the server coalesces those to
 * 'direct' on the way out.
 */
export type ProfileViewSource =
  | 'direct'
  | 'feed'
  | 'hashtag'
  | 'marketplace'
  | 'previously_owned'
  | 'discovery'
  | 'top_profiles'
  | 'leaderboard'
  | 'chat'
  | 'messages'
  | 'provenance'
  | 'user_profiles'
  | 'user_activity'
  | 'follow_list'
  | 'achievements'
  | 'analytics'
  | 'spotlight';

export interface ProfileViewRecord {
  id: string;
  viewedAt: string;
  source: ProfileViewSource | null;
  source_ref: string | null;
  viewer_username: string;
  viewer_display_name: string | null;
  viewer_avatar: string | null;
}

export interface ViewsBySource {
  source: ProfileViewSource;
  views: number;
  uniqueViewers: number;
}

/** Per-spotlight view attribution — joins profile_view via source_ref. */
export interface SpotlightRoiRecord {
  id: string;
  startTime: string;
  endTime: string;
  costKlv: number;
  tokenId: string;
  status: string;
  views: number;
  uniqueViewers: number;
}

export interface ProfileViewsAnalytics {
  stats: {
    totalViews: number;
    uniqueViewers: number;
  };
  bySource: ViewsBySource[];
  spotlightRoi: SpotlightRoiRecord[];
  views: ProfileViewRecord[];
  timeRange: TimeRange;
}

// Server returns one of these per fee type so every type renders even
// when there's no activity yet (#277 acceptance: empty rows show
// "No earnings yet" rather than getting hidden).
export interface FeeTypeStat {
  amount: number;
  count: number;
  /** `false` = the source isn't paying the profile owner yet — UI shows
   *  a "coming soon" hint instead of $0. Currently only spotlight is
   *  flagged unavailable; the rest map to live monetizable interactions. */
  available: boolean;
}

export interface RoyaltiesByType {
  profileSale: FeeTypeStat;
  paidView: FeeTypeStat;
  payPerView: FeeTypeStat;
  subscription: FeeTypeStat;
  dm: FeeTypeStat;
  liveSession: FeeTypeStat;
  consultation: FeeTypeStat;
  spotlightPromotion: FeeTypeStat;
}

export interface RoyaltiesAnalytics {
  stats: {
    total: number;
    period: number;
    breakdown: {
      profileSales: number;
      spotlights: number;
    };
    byType: RoyaltiesByType;
    count: number;
  };
  royalties: RoyaltyRecord[];
  timeRange: TimeRange;
}

/** Per-content click-through row for the share-link analytics surface
 *  (#256). Identifies the shared content (profile by username, post by
 *  uuid) and gives the UI enough label data to render the row without
 *  a second round-trip. */
export interface ShareClickBreakdownRow {
  contentType: 'profile' | 'post';
  contentId: string;
  label: string;
  ownerUsername: string | null;
  clicks: number;
}

export interface ShareClicksAnalytics {
  stats: {
    periodClicks: number;
    totalClicks: number;
    uniqueContent: number;
  };
  breakdown: ShareClickBreakdownRow[];
  timeRange: TimeRange;
}

// -- Public reputation (ADR-008 option B) ---------------------------
// These interfaces MIRROR server/src/services/PublicReputationService.ts
// exactly. Any coefficient / gate / breakdown-shape change bumps
// formulaVersion on the server; the client sees that string in the
// response and can react (today: cosmetic; later: hard version pin).

export interface ReputationScoreBlock<B> {
  value: number;
  computedAt: string;
  breakdown: B;
  /** v1.3 — position among all profiles with a computed score.
   *  `percentile` is null under the server-side small-N threshold
   *  (PERCENTILE_MIN_POPULATION); rank position is always present. */
  rank: {
    position: number;
    total: number;
    percentile: number | null;
  };
}

// v1.2 (2026-08-06): anti-abuse fields (wSybil, wWashLifetime,
// wWash24h) removed from all breakdowns to match the server-side
// redaction. See PublicReputationService FORMULA_VERSION comment
// + ADR-008 amendment for reasoning.

export interface ReputationKlyxValueBreakdown {
  royaltiesTotal: number;
  priceKlv: number;
  wAge: number;
  wKlyxAge: number;
}

export interface ReputationTrendingBreakdown {
  scoreEng7d: number;
  m7d: number;
  scorePrice: number;
  wSpot: number;
}

export interface ReputationHotBreakdown {
  scoreEng2h: number;
  m6h: number;
  decay: number;
  wPostTrade: number;
  wSpot: number;
}

export interface PublicReputationResponse {
  profileId: string;
  profileIdOnChain: number;
  creatorAddress: string;
  scores: {
    klyxValue: ReputationScoreBlock<ReputationKlyxValueBreakdown> | null;
    trendingScore: ReputationScoreBlock<ReputationTrendingBreakdown> | null;
    hotScore: ReputationScoreBlock<ReputationHotBreakdown> | null;
  };
  formulaVersion: string;
  computedAt: string | null;
  replicable: {
    onChainInputs: string[];
    offChainInputs: string[];
    formulaDoc: string;
  };
}

interface AnalyticsState {
  currentCategory: AnalyticsCategory;
  currentTimeRange: TimeRange;

  // Data for each category
  tokenPurchases: {
    data: TokenPurchaseAnalytics[];
    count: number;
    timeRange: TimeRange;
  } | null;

  trades: {
    data: TradeAnalytics[];
    count: number;
    timeRange: TimeRange;
  } | null;

  posts: {
    data: PostAnalytics[];
    count: number;
    timeRange: TimeRange;
  } | null;

  messages: {
    data: MessageAnalytics[];
    count: number;
    sent: number;
    received: number;
    timeRange: TimeRange;
  } | null;

  followers: FollowerAnalytics | null;

  notifications: {
    data: NotificationAnalytics[];
    count: number;
    readCount: number;
    unreadCount: number;
    byType: Record<string, number>;
    timeRange: TimeRange;
  } | null;

  engagement: EngagementAnalytics | null;

  royalties: RoyaltiesAnalytics | null;
  contentEarnings: ContentEarningsAnalytics | null;
  profileViews: ProfileViewsAnalytics | null;
  shareClicks: ShareClicksAnalytics | null;
  /** Public reputation surface (ADR-008 option B). Same shape the
   *  /api/public/reputation endpoint returns — kept unmapped so the
   *  slice mirrors the server contract 1:1 and any formulaVersion
   *  bump is visible to the client without a translation layer. */
  reputation: PublicReputationResponse | null;

  isLoading: boolean;
  error: string | null;
}

const initialState: AnalyticsState = {
  currentCategory: 'royalties',
  currentTimeRange: '30d',
  tokenPurchases: null,
  trades: null,
  posts: null,
  messages: null,
  followers: null,
  notifications: null,
  engagement: null,
  royalties: null,
  contentEarnings: null,
  profileViews: null,
  shareClicks: null,
  reputation: null,
  isLoading: false,
  error: null,
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setCurrentCategory: (state, action: PayloadAction<AnalyticsCategory>) => {
      state.currentCategory = action.payload;
    },

    setCurrentTimeRange: (state, action: PayloadAction<TimeRange>) => {
      state.currentTimeRange = action.payload;
    },

    setTokenPurchases: (state, action: PayloadAction<{ data: TokenPurchaseAnalytics[]; count: number; timeRange: TimeRange }>) => {
      state.tokenPurchases = action.payload;
      state.error = null;
    },

    setTrades: (state, action: PayloadAction<{ data: TradeAnalytics[]; count: number; timeRange: TimeRange }>) => {
      state.trades = action.payload;
      state.error = null;
    },

    setPosts: (state, action: PayloadAction<{ data: PostAnalytics[]; count: number; timeRange: TimeRange }>) => {
      state.posts = action.payload;
      state.error = null;
    },

    setMessages: (state, action: PayloadAction<{ data: MessageAnalytics[]; count: number; sent: number; received: number; timeRange: TimeRange }>) => {
      state.messages = action.payload;
      state.error = null;
    },

    setFollowers: (state, action: PayloadAction<FollowerAnalytics>) => {
      state.followers = action.payload;
      state.error = null;
    },

    setNotifications: (state, action: PayloadAction<{ data: NotificationAnalytics[]; count: number; readCount: number; unreadCount: number; byType: Record<string, number>; timeRange: TimeRange }>) => {
      state.notifications = action.payload;
      state.error = null;
    },

    setEngagement: (state, action: PayloadAction<EngagementAnalytics>) => {
      state.engagement = action.payload;
      state.error = null;
    },

    setRoyalties: (state, action: PayloadAction<RoyaltiesAnalytics>) => {
      state.royalties = action.payload;
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    clearAnalyticsData: (state) => {
      state.tokenPurchases = null;
      state.trades = null;
      state.posts = null;
      state.messages = null;
      state.followers = null;
      state.notifications = null;
      state.engagement = null;
      state.royalties = null;
      state.contentEarnings = null;
      state.profileViews = null;
      state.shareClicks = null;
    },
  },
  extraReducers: (builder) => {
    // Token Purchases
    builder
      .addCase(fetchTokenPurchases.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTokenPurchases.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tokenPurchases = {
          data: action.payload.purchases,
          count: action.payload.count,
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchTokenPurchases.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Trades
    builder
      .addCase(fetchTrades.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrades.fulfilled, (state, action) => {
        state.isLoading = false;
        state.trades = {
          data: action.payload.trades,
          count: action.payload.count,
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchTrades.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Posts
    builder
      .addCase(fetchPosts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.posts = {
          data: action.payload.posts,
          count: action.payload.count,
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Messages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.messages = {
          data: action.payload.messages,
          count: action.payload.count,
          sent: action.payload.sent,
          received: action.payload.received,
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Followers
    builder
      .addCase(fetchFollowers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFollowers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.followers = {
          followers: action.payload.followers,
          following: action.payload.following,
        };
      })
      .addCase(fetchFollowers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = {
          data: action.payload.notifications,
          count: action.payload.count,
          readCount: action.payload.readCount,
          unreadCount: action.payload.unreadCount,
          byType: action.payload.byType,
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Engagement
    builder
      .addCase(fetchEngagement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEngagement.fulfilled, (state, action) => {
        state.isLoading = false;
        state.engagement = {
          likesGiven: action.payload.likesGiven,
          likesReceived: action.payload.likesReceived,
          dislikesGiven: action.payload.dislikesGiven ?? 0,
          dislikesReceived: action.payload.dislikesReceived ?? 0,
          commentsGiven: action.payload.commentsGiven,
          commentsReceived: action.payload.commentsReceived,
          favoritesGiven: action.payload.favoritesGiven,
          // ?? 0 covers the transition window when the server response
          // pre-dates the favoritesReceived rollout (older cached
          // client bundles talking to the newer API get the field;
          // newer client bundles talking to an older API don't).
          favoritesReceived: action.payload.favoritesReceived ?? 0,
        };
      })
      .addCase(fetchEngagement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Content Earnings (PPV + future view fees/subscriptions)
    builder
      .addCase(fetchContentEarnings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchContentEarnings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contentEarnings = {
          stats: action.payload.stats,
          sales: action.payload.sales,
          viewFeeSales: action.payload.viewFeeSales ?? [],
          subscriptionSales: action.payload.subscriptionSales ?? [],
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchContentEarnings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Profile Views
    builder
      .addCase(fetchProfileViews.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfileViews.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileViews = {
          stats: action.payload.stats,
          bySource: action.payload.bySource ?? [],
          spotlightRoi: action.payload.spotlightRoi ?? [],
          views: action.payload.views,
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchProfileViews.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Royalties
    builder
      .addCase(fetchRoyalties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoyalties.fulfilled, (state, action) => {
        state.isLoading = false;
        state.royalties = {
          stats: action.payload.stats,
          royalties: action.payload.royalties,
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchRoyalties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Share-link click-throughs (#256)
    builder
      .addCase(fetchShareClicks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShareClicks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shareClicks = {
          stats: action.payload.stats,
          breakdown: action.payload.breakdown ?? [],
          timeRange: action.payload.timeRange,
        };
      })
      .addCase(fetchShareClicks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Public reputation (ADR-008 option B). Slice mirrors the server
    // response 1:1 — see PublicReputationResponse comment above.
    builder
      .addCase(fetchReputation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReputation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.reputation = action.payload;
      })
      .addCase(fetchReputation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentCategory,
  setCurrentTimeRange,
  setTokenPurchases,
  setTrades,
  setPosts,
  setMessages,
  setFollowers,
  setNotifications,
  setEngagement,
  setRoyalties,
  setLoading,
  setError,
  clearError,
  clearAnalyticsData,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
